import type { components } from "@oathera/contract/api";
import type { Measure } from "../../components/index.ts";
import type {
  EnforcementSummary,
  ResourceVerificationRow,
  ReplayRow,
  GatewayStageRow,
} from "./use-enforcement.ts";

/**
 * Presentation vocabulary for the Enforcement screen (task 11.8; design 12.2 Enforcement row, 12.3
 * principles). It keeps the one-way maps from contract shapes onto the screen's presentational
 * pieces in a single, testable place and defines NO API shapes (those come from
 * `@oathera/contract/api` via `use-enforcement.ts`):
 *
 * - how a `Quantity`'s exactness becomes an exact/estimate/unverifiable `Measure` (design 12.3);
 * - human labels for verification paths and error codes, replay namespaces, Gateway stages and
 *   Gateway error codes;
 * - the catalog ORDERS and the always-present-with-zeros derivations that make the named,
 *   testable guarantee explicit (FR-5.2, FR-5.3): the replay-by-namespace breakdown and the
 *   Gateway stage/code breakdown are reconciled against the frozen catalogs so EVERY catalog value
 *   is rendered as a row/cell — with an explicit ZERO when the server reported nothing for it —
 *   rather than dropping zero rows. The contract already zero-fills these (one entry per catalog
 *   value), so the derivations normally just order the server's rows; the fallback to an explicit
 *   zero is belt-and-braces so a short payload still shows a complete, honest grid;
 * - the derivation that surfaces the `invalid_assertion` verification outcomes as the screen's
 *   "assertion failures" (design 12.2 lists "assertion failures"; the contract carries them as the
 *   `invalid_assertion` verification error code — there is NO separate `assertion_failures` field);
 * - the drill-down `/events` query strings. The enforcement summary attaches NO server-built `drill`
 *   to its rows (unlike the decisions breakdown), so the screen composes honest standing filters
 *   from real `/events` params — never an invented route. The events screen (task 11.12) reads them.
 */

type Quantity = components["schemas"]["Quantity"];
type VerificationPath = components["schemas"]["enum_verification_path"];
type VerificationErrorCode = components["schemas"]["enum_verification_error_code"];
type ReplayNamespace = components["schemas"]["enum_replay_namespace"];
type GatewayStage = components["schemas"]["enum_gateway_stage"];
type GatewayErrorCode = components["schemas"]["enum_gateway_error_code"];

/** The verification error code that IS an assertion failure (design 12.2 "assertion failures"). */
export const ASSERTION_FAILURE_CODE: VerificationErrorCode = "invalid_assertion";

/** Map a `Quantity`'s exactness onto the shared exact/estimate vocabulary (design 12.3). */
export function quantityMeasure(q: Quantity): Measure {
  // No data cannot be verified: neither an exact counter nor a sampled estimate, so never green.
  if (q.value === null) return "unverifiable";
  return q.exactness === "estimated" ? "estimate" : "exact";
}

/** A `Quantity`'s numeric value treated as zero when the server reported no data (null). */
export function quantityValue(q: Quantity): number {
  return q.value ?? 0;
}

/** An explicit, exact zero `Quantity` used to fill a catalog value the server omitted. */
export function zeroQuantity(): Quantity {
  return { value: 0, exactness: "exact" };
}

/* ---- Catalog orders (the frozen presentation order; drives always-present rows) ------------- */

/** Verification paths in a stable presentation order. */
export const VERIFICATION_PATH_ORDER: readonly VerificationPath[] = ["direct", "gateway"];

/**
 * Verification error codes in a stable presentation order (success `none` first, then failures).
 * Mirrors the `enum_verification_error_code` catalog; kept here so the resource-verification table
 * reads consistently regardless of the order the server returned rows in.
 */
export const VERIFICATION_CODE_ORDER: readonly VerificationErrorCode[] = [
  "none",
  "invalid_token",
  "token_expired",
  "invalid_proof",
  "proof_replayed",
  "audience_mismatch",
  "invalid_assertion",
  "agent_revoked",
  "other",
];

/**
 * The six replay namespaces in presentation order. This is the catalog the always-present
 * guarantee is checked against (FR-5.2): the breakdown must show one row per value here, zero when
 * none fired. Mirrors `enum_replay_namespace`.
 */
export const REPLAY_NAMESPACE_ORDER: readonly ReplayNamespace[] = [
  "operation_proof",
  "gateway_assertion",
  "identity_token",
  "decision",
  "enrollment",
  "other",
];

/**
 * The eight Gateway pipeline stages in order (the Gateway's pipeline flow, `other` last). This is
 * the catalog the always-present guarantee is checked against (FR-5.3). Mirrors `enum_gateway_stage`.
 */
export const GATEWAY_STAGE_ORDER: readonly GatewayStage[] = [
  "parse",
  "credentials",
  "decision_request",
  "decision_verify",
  "authorize",
  "handler",
  "forward",
  "other",
];

/**
 * The five Gateway error codes in order (`other` last). Every stage carries a cell per code; this
 * is the per-stage catalog the always-present guarantee fills (FR-5.3). Mirrors
 * `enum_gateway_error_code`.
 */
export const GATEWAY_CODE_ORDER: readonly GatewayErrorCode[] = [
  "gateway_audit_failure",
  "decision_client_error",
  "handler_exception",
  "config_invalid",
  "other",
];

/* ---- Human labels --------------------------------------------------------------------------- */

/** Human label for a verification path (how a Resource Server request arrived). */
export function verificationPathLabel(path: VerificationPath): string {
  return path === "direct" ? "Direct" : "Via Gateway";
}

/** Human label for a Resource Server verification error code (`none` is the success label). */
export function verificationCodeLabel(code: VerificationErrorCode): string {
  switch (code) {
    case "none":
      return "Verified";
    case "invalid_token":
      return "Invalid token";
    case "token_expired":
      return "Token expired";
    case "invalid_proof":
      return "Invalid proof";
    case "proof_replayed":
      return "Proof replayed";
    case "audience_mismatch":
      return "Audience mismatch";
    case "invalid_assertion":
      return "Invalid assertion";
    case "agent_revoked":
      return "Agent revoked";
    case "other":
      return "Other";
  }
}

/** Human label for a replay store namespace. */
export function replayNamespaceLabel(namespace: ReplayNamespace): string {
  switch (namespace) {
    case "operation_proof":
      return "Operation proof";
    case "gateway_assertion":
      return "Gateway assertion";
    case "identity_token":
      return "Identity token";
    case "decision":
      return "Decision";
    case "enrollment":
      return "Enrollment";
    case "other":
      return "Other";
  }
}

/** Human label for a Gateway pipeline stage. */
export function gatewayStageLabel(stage: GatewayStage): string {
  switch (stage) {
    case "parse":
      return "Parse";
    case "credentials":
      return "Credentials";
    case "decision_request":
      return "Decision request";
    case "decision_verify":
      return "Decision verify";
    case "authorize":
      return "Authorize";
    case "handler":
      return "Handler";
    case "forward":
      return "Forward";
    case "other":
      return "Other";
  }
}

/** Human label for a Gateway internal error code. */
export function gatewayCodeLabel(code: GatewayErrorCode): string {
  switch (code) {
    case "gateway_audit_failure":
      return "Audit failure";
    case "decision_client_error":
      return "Decision client error";
    case "handler_exception":
      return "Handler exception";
    case "config_invalid":
      return "Config invalid";
    case "other":
      return "Other";
  }
}

/* ---- Always-present-with-zeros derivations (FR-5.2, FR-5.3) --------------------------------- */

/** A replay row reconciled against the catalog: a count that is always present (zero when none). */
export interface ReplayView {
  readonly namespace: ReplayNamespace;
  readonly count: Quantity;
}

/**
 * Reconcile the replay-by-namespace breakdown against the frozen six-namespace catalog so EVERY
 * namespace is rendered as a real row, with an explicit zero when the server reported none (FR-5.2).
 * The contract guarantees the server already zero-fills to the catalog; this derivation ORDERS the
 * server's rows into the presentation order and fills any missing namespace with an explicit zero
 * (belt-and-braces for a short payload), so a complete grid is always shown — never a hidden or
 * collapsed empty state. An all-zero result is a legitimate "no replay detections" READY view.
 */
export function deriveReplayViews(rows: readonly ReplayRow[]): readonly ReplayView[] {
  const byNamespace = new Map<ReplayNamespace, Quantity>();
  for (const row of rows) byNamespace.set(row.namespace, row.count);
  return REPLAY_NAMESPACE_ORDER.map((namespace) => ({
    namespace,
    count: byNamespace.get(namespace) ?? zeroQuantity(),
  }));
}

/** A single Gateway stage/code cell: a count that is always present (zero when none). */
export interface GatewayCell {
  readonly code: GatewayErrorCode;
  readonly count: Quantity;
}

/** A Gateway stage reconciled against the catalog: a total and one cell per error code. */
export interface GatewayStageView {
  readonly stage: GatewayStage;
  readonly total: Quantity;
  /** One cell per Gateway error code, in catalog order; always present (zero when none). */
  readonly cells: readonly GatewayCell[];
}

/**
 * Reconcile the Gateway stage/code breakdown against the frozen eight-stage × five-code catalog so
 * EVERY stage is a row and EVERY code is a cell, with explicit zeros when the server reported none
 * (FR-5.3). Like {@link deriveReplayViews} this orders the server's rows and fills any missing
 * stage/code with an explicit zero. The whole grid is therefore always rendered; an all-zero result
 * is the legitimate "no Gateway pipeline failures" READY view, not an empty/hidden state.
 */
export function deriveGatewayStageViews(
  rows: readonly GatewayStageRow[],
): readonly GatewayStageView[] {
  const byStage = new Map<GatewayStage, GatewayStageRow>();
  for (const row of rows) byStage.set(row.stage, row);
  return GATEWAY_STAGE_ORDER.map((stage) => {
    const row = byStage.get(stage);
    const byCode = new Map<GatewayErrorCode, Quantity>();
    if (row !== undefined) for (const cell of row.by_code) byCode.set(cell.code, cell.count);
    return {
      stage,
      total: row?.total ?? zeroQuantity(),
      cells: GATEWAY_CODE_ORDER.map((code) => ({
        code,
        count: byCode.get(code) ?? zeroQuantity(),
      })),
    };
  });
}

/**
 * A verification outcome row for the resource-verification table: the path, the code and the count.
 * Rows are ordered by (code, path) so the table reads consistently; the resource-verification array
 * is NOT catalog-complete by contract (only observed path/code pairs appear), so this derivation
 * simply orders what the server returned — it does not invent rows for unobserved pairs.
 */
export interface VerificationView {
  readonly path: VerificationPath;
  readonly code: VerificationErrorCode;
  readonly count: Quantity;
}

/** Order the resource-verification rows by code (catalog order) then path, for a stable table. */
export function deriveVerificationViews(
  rows: readonly ResourceVerificationRow[],
): readonly VerificationView[] {
  const codeRank = (c: VerificationErrorCode): number => VERIFICATION_CODE_ORDER.indexOf(c);
  const pathRank = (p: VerificationPath): number => VERIFICATION_PATH_ORDER.indexOf(p);
  return [...rows]
    .map((r) => ({ path: r.path, code: r.code, count: r.count }))
    .sort((a, b) => codeRank(a.code) - codeRank(b.code) || pathRank(a.path) - pathRank(b.path));
}

/**
 * The assertion-failure view (design 12.2 "assertion failures"). The contract has no dedicated
 * assertion field; an assertion failure is a Resource Server verification with code
 * `invalid_assertion`. This selects exactly those rows (any path) so the screen can surface them as
 * a highlighted subset of the verification outcomes rather than inventing an API field.
 */
export function deriveAssertionFailures(
  rows: readonly ResourceVerificationRow[],
): readonly VerificationView[] {
  return deriveVerificationViews(rows).filter((r) => r.code === ASSERTION_FAILURE_CODE);
}

/* ---- Emptiness (the healthy all-zeros READY state) ------------------------------------------ */

/**
 * Whether the WHOLE summary is empty — no verification rows AND every always-present breakdown cell
 * is zero/null. This is NOT the common case: because the two breakdowns are always present and the
 * server may legitimately return an all-zero grid, an all-zero summary is a healthy "no enforcement
 * failures" READY view that renders zeros, not the scaffold's empty state. The screen only treats a
 * summary as empty when there is genuinely nothing at all to describe (used to distinguish a true
 * no-data window from a healthy quiet one in the state resolver).
 */
export function isSummaryEmpty(summary: EnforcementSummary): boolean {
  const noVerification = summary.resource_verification.every((r) => quantityValue(r.count) === 0);
  const noReplay = summary.replay_by_namespace.every((r) => quantityValue(r.count) === 0);
  const noGateway = summary.gateway_stage_failures.every(
    (s) => quantityValue(s.total) === 0 && s.by_code.every((c) => quantityValue(c.count) === 0),
  );
  return noVerification && noReplay && noGateway;
}

/* ---- Drill-down to /events ------------------------------------------------------------------ */

/**
 * Build a relative `/events` link from a set of filter params. Values are encoded by
 * `URLSearchParams`; empty values are dropped. The enforcement endpoint does not attach a
 * server-built `drill`, so the screen composes honest standing filters from real `/events` params —
 * never an invented route. The events screen (task 11.12) reads these params.
 */
function eventsHref(params: Readonly<Record<string, string>>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value.length > 0) search.set(key, value);
  }
  const qs = search.toString();
  return qs.length > 0 ? `/events?${qs}` : "/events";
}

/**
 * The `/events` filter for a Resource Server verification outcome (path + code). `category=
 * verification` plus the path and code are faithful standing filters, so each verification row
 * drills to exactly the events behind it (design 12.3). `none` is the verified-success label and is
 * not a failure, but it still drills honestly to its events.
 */
export function verificationHref(path: VerificationPath, code: VerificationErrorCode): string {
  return eventsHref({ category: "verification", path, code });
}

/** The `/events` filter for the assertion failures (verification code `invalid_assertion`). */
export function assertionFailureHref(path: VerificationPath): string {
  return eventsHref({ category: "verification", path, code: ASSERTION_FAILURE_CODE });
}

/** The `/events` filter for a replay detection in a given namespace. */
export function replayHref(namespace: ReplayNamespace): string {
  return eventsHref({ category: "replay", namespace });
}

/** The `/events` filter for a Gateway pipeline failure at a stage, optionally a specific code. */
export function gatewayHref(stage: GatewayStage, code: GatewayErrorCode | null): string {
  const base: Record<string, string> = { category: "gateway", stage };
  if (code !== null) base["code"] = code;
  return eventsHref(base);
}
